import React from 'react';
import { OrderCreationItemDto, OrderResponseDto } from '@atlassian/commerce-ccp-gateway-client';
import { placePartnerOrder } from '@atlassian/temp-proxies';
import { v4 as generateUuid } from 'uuid';
import { usePlaceOrderNotification } from './use-place-order-notification';
import { PartnerQuoteLineItems } from '../types';

export type PlaceOrderParams = {
  orderIntentId: string;
  invoiceGroupId?: string;
  transactionAccountId: string;
  items: OrderCreationItemDto[];
  endCustomerContacts: Array<string>;
  siteId?: string;
  // eslint-disable-next-line no-unused-vars
  onOrderPlaced: (orderResponse: OrderResponseDto) => void;
};

export const usePlaceOrder = (): {
  orderId: string;
  orderBeingPlaced: boolean;
  errored: boolean;
  processOrderCreationItemsForOrder: (
    // eslint-disable-next-line no-unused-vars
    lineItems: PartnerQuoteLineItems,
    // eslint-disable-next-line no-unused-vars
    orderCreationItems: OrderCreationItemDto[],
    // eslint-disable-next-line no-unused-vars
    nextBillingAnchorTimestamp: number | undefined
  ) => OrderCreationItemDto[];
  // eslint-disable-next-line no-unused-vars
  placeOrder: (params: PlaceOrderParams) => Promise<void>;
} => {
  const [orderBeingPlaced, setOrderBeingPlaced] = React.useState(false);
  const [errored, setErrored] = React.useState(false);
  const [orderId] = React.useState(generateUuid());

  const { notifyFailure } = usePlaceOrderNotification();

  const placeOrder = async ({
    invoiceGroupId,
    items,
    transactionAccountId,
    endCustomerContacts,
    onOrderPlaced,
    siteId,
  }: PlaceOrderParams) => {
    setOrderBeingPlaced(true);
    setErrored(false);
    try {
      const response = await placePartnerOrder({
        orderId,
        invoiceGroupId,
        transactionAccountId,
        items,
        metadata: { ecc_aaids: endCustomerContacts.join(', '), ...(siteId && { siteId }) },
      });
      onOrderPlaced(response);
    } catch (err: any) {
      setErrored(true);
      notifyFailure(err.message);
    } finally {
      setOrderBeingPlaced(false);
    }
  };

  const processOrderCreationItemsForOrder = (
    lineItems: PartnerQuoteLineItems,
    orderCreationItems: OrderCreationItemDto[],
    nextBillingAnchorTimestamp: number | undefined
  ) => {
    const timestamp = Date.now();
    const freeItemIds = lineItems.filter((lineItem) => lineItem.isFreeProduct).map((lineItem) => lineItem.lineItemId);
    const processedOrderCreationItems = orderCreationItems.map((orderCreationItem) => {
      if (freeItemIds.includes(orderCreationItem.itemId)) {
        return {
          ...orderCreationItem,
          nextBillingAnchorTimestamp: nextBillingAnchorTimestamp ?? timestamp,
          optedUsageOptions: {
            ...orderCreationItem.optedUsageOptions,
            // Free items should not have trial
            trial: undefined,
          },
        };
      }
      return { ...orderCreationItem, ...{ nextBillingAnchorTimestamp: nextBillingAnchorTimestamp ?? timestamp } };
    });
    return processedOrderCreationItems.map((item) => {
      const chargingDetails = item.optedUsageOptions?.chargingDetails;
      if (chargingDetails) {
        const chargeQuantities =
          chargingDetails.chargeQuantities?.map((chargeQuantity) => ({
            ...chargeQuantity,
            quantity: 1,
          })) ?? [];
        return {
          ...item,
          optedUsageOptions: {
            ...item.optedUsageOptions,
            chargingDetails: {
              ...chargingDetails,
              chargeQuantities,
            },
          },
        };
      }
      return item;
    });
  };

  return {
    orderId,
    orderBeingPlaced,
    errored,
    placeOrder,
    processOrderCreationItemsForOrder,
  };
};
